"use client";

import { useEffect, useState } from "react";
import { axiosClient } from "@/lib/axiosClient";
import { useActivePeriod } from "@/hooks/useActivePeriod";
import { Loader2, Package, AlertTriangle, Info } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import InfoKonvensiFessModal from "../paket/InfoKonvensiFessModal";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import toast from "react-hot-toast";

interface PackageItem {
    package_id: number;
    name: string;
    price: string;
    is_active: number | boolean;
    start_date?: string;
    end_date?: string;
}

interface PackagePriceListProps {
    showTitle?: boolean;
    compact?: boolean;
}

export default function PackagePriceList({
    showTitle = false,
    compact = false,
}: PackagePriceListProps) {
    const [packages, setPackages] = useState<PackageItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const { data: activePeriod, isLoading: loadingPeriod, isError } = useActivePeriod();

    useEffect(() => {
        if (activePeriod?.id) {
            void fetchPackages(activePeriod.id);
        }
    }, [activePeriod]);

    const fetchPackages = async (periodId: number): Promise<void> => {
        try {
            setLoading(true);
            setError(false);
            const { data } = await axiosClient.get<any[]>(
                `/packages/period/${periodId}`
            );

            const flatList: any[] = data.flatMap((pkg) =>
                pkg.children?.length
                    ? pkg.children.map((child: any) => ({
                        ...child,
                        package_id: child.package_id,
                        name: child.name,
                        price: child.price,
                        is_active: child.is_active,
                        start_date: child.start_date,
                        end_date: child.end_date,
                        description: child.description,
                        itineraries: child.itineraries,
                        photo_url: child.photo_path || child.photo_url,
                    }))
                    : [
                        {
                            ...pkg,
                            package_id: pkg.package_id,
                            name: pkg.name,
                            price: pkg.price,
                            is_active: pkg.is_active,
                            start_date: pkg.start_date,
                            end_date: pkg.end_date,
                            description: pkg.description,
                            itineraries: pkg.itineraries,
                            photo_url: pkg.photo_path || pkg.photo_url,
                        },
                    ]
            );
            const activePackages = flatList.filter(
                (pkg) => pkg.is_active === 1 || pkg.is_active === true
            );

            setPackages(activePackages);
        } catch (e) {
            console.error("Gagal memuat daftar paket:", e);
            toast.error("Gagal memuat daftar paket.");
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    const [selectedPackage, setSelectedPackage] = useState<any | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleOpenModal = (pkg: any) => {
        setSelectedPackage(pkg);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setTimeout(() => setSelectedPackage(null), 300);
    };

    if (loadingPeriod) {
        return (
            <div className="flex items-center justify-center py-6 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Memuat periode aktif...
            </div>
        );
    }

    if (isError || error) {
        return (
            <div className="flex items-center justify-center gap-2 text-red-500 py-4">
                <AlertTriangle className="w-5 h-5" /> Gagal memuat paket.
            </div>
        );
    }

    // Format tanggal sederhana
    const formatDate = (dateStr?: string) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    return (
        <div className="space-y-4">
            {showTitle && (
                <div className="flex items-center gap-2 mb-2">
                    <Package className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                        Daftar Paket & Harga ({activePeriod?.name || "-"})
                    </h3>
                </div>
            )}

            <Card
                className={`border border-gray-200 dark:border-gray-700 shadow-sm overflow-y-auto ${compact ? "max-h-[50vh]" : "max-h-[60vh]"}`}
            >
                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="py-8 text-center text-gray-500">
                            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                            Memuat daftar paket...
                        </div>
                    ) : (
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-gray-50 dark:bg-gray-800">
                                <TableHead className="w-20 text-center">ID Paket</TableHead>
                                <TableHead>Nama Paket</TableHead>
                                <TableHead>Tanggal Pelaksanaan</TableHead>
                                <TableHead className="w-16"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {packages.length > 0 ? (
                                packages.map((pkg: any) => (
                                    <TableRow
                                        key={pkg.package_id}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-800 transition group"
                                    >
                                        <TableCell className="text-center font-medium text-indigo-600">
                                            {pkg.package_id}
                                        </TableCell>
                                        <TableCell>{pkg.name}</TableCell>
                                        <TableCell className="text-gray-600 dark:text-gray-400 text-sm">
                                            {pkg.start_date ? (
                                                <>
                                                    {formatDate(pkg.start_date)}
                                                    {pkg.end_date && pkg.end_date !== pkg.start_date ? ` - ${formatDate(pkg.end_date)}` : ''}
                                                </>
                                            ) : (
                                                "-"
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <TooltipProvider delayDuration={200}>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            onClick={() => handleOpenModal(pkg)}
                                                            className="text-gray-400 hover:text-blue-600 p-1.5 rounded-full hover:bg-blue-50 transition-colors focus:outline-none"
                                                        >
                                                            <Info className="w-5 h-5" />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>Lihat Detail Paket</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center text-gray-500 py-4">
                                        Tidak ada paket tersedia.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    )}
                </div>
            </Card>

            {/* Modal Detail Paket */}
            {isModalOpen && selectedPackage && (
                <InfoKonvensiFessModal
                    pkg={selectedPackage}
                    onClose={handleCloseModal}
                />
            )}
        </div>
    );
}
